import * as zExt from "@redotech/rpc/ext";
import { nullishEqual, numberEqual } from "@redotech/util/equal";
import { OmitUnion } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import {
  PriceConstraint,
  PriceConstraintInequality,
  PriceConstraintKind,
  PriceConstraintSchema,
} from "../integration/redo-in-shipping/rate-table-constraints";
import { DynamicRateRounding } from "../integration/redo-in-shipping/shipping-rate-table";

export enum MercuryModelMode {
  ENABLED = "ENABLED",
  LITE_ENABLED = "LITE_ENABLED",
  SHADOW = "SHADOW",
  DISABLED = "DISABLED",
}

export function isActiveMercuryModelMode(mode: MercuryModelMode): boolean {
  return (
    mode === MercuryModelMode.ENABLED || mode === MercuryModelMode.LITE_ENABLED
  );
}

const priceFeatureDefinitionSchema = z.object({
  featureKind: z.literal("PRICE"),
  rateTableId: z.string(),
});

const deliveryDaysFeatureDefinitionSchema = z.object({
  featureKind: z.literal("DELIVERY_DAYS"),
  rateTableId: z.string(),
});

const visibilityFeatureDefinitionSchema = z.object({
  featureKind: z.literal("VISIBILITY"),
  rateTableId: z.string(),
});

const contextFeatureDefinitionSchema = z.object({
  featureKind: z.literal("CONTEXT"),
  contextKind: createEnum(
    [
      "CART_VALUE_LOG_NORMALIZED",
      "NUM_CART_ITEMS",
      "DAY_OF_WEEK_SIN",
      "DAY_OF_WEEK_COS",
      "TIME_OF_DAY_SIN",
      "TIME_OF_DAY_COS",
    ],
    { enumName: "MercuryContextFeatureKind" },
  ),
});

const featureDefinitionSchema = z.union([
  priceFeatureDefinitionSchema,
  deliveryDaysFeatureDefinitionSchema,
  visibilityFeatureDefinitionSchema,
  contextFeatureDefinitionSchema,
]);
export type FeatureDefinition = z.infer<typeof featureDefinitionSchema>;

const MercuryModelConfigSchema_ = z.object({
  rateTableConfigByRateTableId: z.record(
    z.string(), // rate table id
    z.object({
      isConditionallyDisplayed: z.boolean(),
      isDeliveryDateDisplayed: z.boolean(),
      rounding: createEnum(DynamicRateRounding, {
        enumName: "DynamicRateRounding",
      }).nullish(),
      priceConstraints: PriceConstraintSchema.nullish(),
    }),
  ),
  featureDefinitions: z.array(featureDefinitionSchema),
});
export interface MercuryModelConfig
  extends z.infer<typeof MercuryModelConfigSchema_> {}

export enum MercuryModelConfigEventKind {
  SAVED_RATE_TABLE_CONFIG = "SAVED_RATE_TABLE_CONFIG",
}

const savedRateTableConfigEventSchema = z.object({
  eventKind: z.literal(MercuryModelConfigEventKind.SAVED_RATE_TABLE_CONFIG),
  versionId: z.string(),
  config: MercuryModelConfigSchema_,
  timestamp: z.date(),
});
export interface SavedRateTableConfigEvent
  extends z.infer<typeof savedRateTableConfigEventSchema> {}

const mercuryModelConfigEventSchema = z.union([
  savedRateTableConfigEventSchema,
]);

export enum MercuryModelTrainingEventKind {
  TRAINED_AND_DEPLOYED = "TRAINED_AND_DEPLOYED",
}

const trainedAndDeployedEventSchema = z.object({
  eventKind: z.literal(MercuryModelTrainingEventKind.TRAINED_AND_DEPLOYED),
  configVersionId: z.string(),
  timestamp: z.date(),
});

const mercuryModelTrainingEventSchema = z.union([
  trainedAndDeployedEventSchema,
]);

const BaseMercuryModelSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  checkoutExperienceId: zExt.objectId(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const MercuryTrainingStatus = ["active", "training", "failed"] as const;

const FullMercuryModelSchema_ = BaseMercuryModelSchema_.extend({
  config: MercuryModelConfigSchema_,

  configEventHistory: z.array(mercuryModelConfigEventSchema).nullish(),

  trainingEventHistory: z.array(mercuryModelTrainingEventSchema).nullish(),

  hasRecordedData: z.boolean().nullish(),

  mode: z
    .union([
      z.literal(MercuryModelMode.ENABLED),
      z.literal(MercuryModelMode.SHADOW),
      z.literal(MercuryModelMode.DISABLED),
    ])
    .nullish(),

  assumedOrderMargin: z
    .object({ percentage: z.number().optional(), fixed: z.number().optional() })
    .nullish(),

  // Training lifecycle fields
  trainingStatus: createEnum(MercuryTrainingStatus, {
    enumName: "MercuryTrainingStatus",
  }).nullish(),
  trainCount: z.number().int().nullish(),
  lastTrained: z.date().nullish(),
  nextTrain: z.date().nullish(),
  consecutiveFailures: z.number().int().nullish(),
  lastFailureReason: z.string().nullish(),
});

const MercuryLiteConfigSchema_ = z.object({
  /** Order total (in shop currency) at which the scale factor saturates at 1.0 */
  scaleMax: z.number().positive().optional(),
  /** Exponent for scale factor curve: 1 = linear, <1 = concave, >1 = convex */
  exponent: z.number().optional(),
});
export const MercuryLiteConfigSchema: z.ZodType<MercuryLiteConfig> =
  MercuryLiteConfigSchema_;
export interface MercuryLiteConfig
  extends z.infer<typeof MercuryLiteConfigSchema_> {}

const LiteMercuryModelSchema_ = BaseMercuryModelSchema_.extend({
  mode: z.literal(MercuryModelMode.LITE_ENABLED),
  liteConfig: MercuryLiteConfigSchema_.optional(),
});

export const MercuryModelSchema = z.union([
  FullMercuryModelSchema_,
  LiteMercuryModelSchema_,
]);
export type MercuryModel = z.infer<typeof MercuryModelSchema>;
export type CreateMercuryModel = OmitUnion<
  MercuryModel,
  "_id" | "createdAt" | "updatedAt"
>;

export function areRateTableConfigsEqual(
  a: MercuryModelConfig["rateTableConfigByRateTableId"],
  b: MercuryModelConfig["rateTableConfigByRateTableId"],
): boolean {
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();

  // Check if they have the same rate table IDs
  if (
    keysA.length !== keysB.length ||
    !keysA.every((key, index) => key === keysB[index])
  ) {
    return false;
  }

  // Compare each rate table config
  return keysA.every((rateTableId) => {
    const configA = a[rateTableId];
    const configB = b[rateTableId];

    return (
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      configA.isConditionallyDisplayed === configB.isConditionallyDisplayed &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      configA.isDeliveryDateDisplayed === configB.isDeliveryDateDisplayed &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      configA.rounding === configB.rounding &&
      arePriceConstraintsEqual(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        configA.priceConstraints,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        configB.priceConstraints,
      )
    );
  });
}

function arePriceConstraintsEqual(
  a: MercuryModelConfig["rateTableConfigByRateTableId"][string]["priceConstraints"],
  b: MercuryModelConfig["rateTableConfigByRateTableId"][string]["priceConstraints"],
): boolean {
  // Both null/undefined
  if (!a && !b) {
    return true;
  }

  // One is null/undefined, the other is not
  if (!a || !b) {
    return false;
  }

  // Both have values - compare them
  if (a.kind !== b.kind || a.currency !== b.currency) {
    return false;
  }

  if (
    a.kind === PriceConstraintKind.DEFAULT &&
    b.kind === PriceConstraintKind.DEFAULT
  ) {
    return areDefaultPriceConstraintsEqual(a, b);
  }

  if (
    a.kind === PriceConstraintKind.RELATIVE_TO_OTHER_RATE_TABLE &&
    b.kind === PriceConstraintKind.RELATIVE_TO_OTHER_RATE_TABLE
  ) {
    return areRelativePriceConstraintsEqual(a, b);
  }

  return false;
}

function areDefaultPriceConstraintsEqual(
  a: Extract<PriceConstraint, { kind: PriceConstraintKind.DEFAULT }>,
  b: Extract<PriceConstraint, { kind: PriceConstraintKind.DEFAULT }>,
): boolean {
  // Compare absolute price bounds
  const priceConstraintsEqual = areAbsolutePriceBoundsEqual(
    a.priceConstraints,
    b.priceConstraints,
  );

  // Compare margin constraints
  const marginConstraintsEqual = areMarginConstraintsEqual(
    a.marginConstraints,
    b.marginConstraints,
  );

  return priceConstraintsEqual && marginConstraintsEqual;
}

function areRelativePriceConstraintsEqual(
  a: Extract<
    PriceConstraint,
    { kind: PriceConstraintKind.RELATIVE_TO_OTHER_RATE_TABLE }
  >,
  b: Extract<
    PriceConstraint,
    { kind: PriceConstraintKind.RELATIVE_TO_OTHER_RATE_TABLE }
  >,
): boolean {
  // Compare relative price constraints
  const priceConstraintsEqual =
    a.priceConstraints.relativeToRateTableId ===
      b.priceConstraints.relativeToRateTableId &&
    arePriceInequalitiesEqual(
      a.priceConstraints.byAtLeast,
      b.priceConstraints.byAtLeast,
    ) &&
    arePriceInequalitiesEqual(
      a.priceConstraints.byAtMost,
      b.priceConstraints.byAtMost,
    );

  // Compare margin constraints
  const marginConstraintsEqual = areMarginConstraintsEqual(
    a.marginConstraints,
    b.marginConstraints,
  );

  return priceConstraintsEqual && marginConstraintsEqual;
}

function areAbsolutePriceBoundsEqual(
  a:
    | {
        atLeast?: { amount?: number | null } | null;
        atMost?: { amount?: number | null } | null;
      }
    | null
    | undefined,
  b:
    | {
        atLeast?: { amount?: number | null } | null;
        atMost?: { amount?: number | null } | null;
      }
    | null
    | undefined,
): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  return (
    nullishEqual(numberEqual)(a.atLeast?.amount, b.atLeast?.amount) &&
    nullishEqual(numberEqual)(a.atMost?.amount, b.atMost?.amount)
  );
}

function areMarginConstraintsEqual(
  a:
    | {
        atLeast?: PriceConstraintInequality | null;
        atMost?: PriceConstraintInequality | null;
      }
    | null
    | undefined,
  b:
    | {
        atLeast?: PriceConstraintInequality | null;
        atMost?: PriceConstraintInequality | null;
      }
    | null
    | undefined,
): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  return (
    arePriceInequalitiesEqual(a.atLeast, b.atLeast) &&
    arePriceInequalitiesEqual(a.atMost, b.atMost)
  );
}

function arePriceInequalitiesEqual(
  a: PriceConstraintInequality | null | undefined,
  b: PriceConstraintInequality | null | undefined,
): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  return (
    nullishEqual(numberEqual)(a.amount, b.amount) &&
    nullishEqual(numberEqual)(a.percentage, b.percentage)
  );
}
